import { masterDataChannel } from '@rb/shared/master-data/cdn-config';
import { getSheetEnv, getSheetsBase } from '@/lib/cdnBase';

/**
 * R2(마스터데이터 결과 JSON) 에서 단일 client 컬렉션을 받아온다.
 *
 * 클라 게임이 쓰는 것과 동일한 산출물: `<sheetsBase>/<env>/manifest.json` 으로
 * 버전을 찾고 `<sheetsBase>/<env>/<key>.<version>.json` 을 fetch.
 *
 * verse 의 정적 RPC(`pet.getPets` 등)는 codec 으로 인코딩된 positional 튜플을
 * 반환하고 표시 이름이 없으므로, 보상 피커는 이름이 살아있는 R2 readable
 * 컬렉션(`clientPets`/`clientCharacters`)을 직접 쓴다.
 */
interface SheetManifest {
  collections?: Record<string, { version: string }>;
}

export async function fetchSheetCollection<T>(
  cdnKey: string,
  fetchImpl: typeof fetch = fetch
): Promise<T> {
  const base = getSheetsBase();
  // 'production' sheet env 는 'prod' R2 채널에 발행되므로 매핑(그 외 identity).
  const env = masterDataChannel(getSheetEnv());

  const manifestRes = await fetchImpl(`${base}/${env}/manifest.json`, { cache: 'no-store' });
  if (!manifestRes.ok) {
    throw new Error(`sheet manifest fetch failed for env "${env}" (${manifestRes.status})`);
  }
  const manifest = (await manifestRes.json()) as SheetManifest;
  const entry = manifest.collections?.[cdnKey];
  if (!entry) {
    throw new Error(`fetchSheetCollection: "${cdnKey}" not in manifest for env "${env}"`);
  }

  const res = await fetchImpl(`${base}/${env}/${cdnKey}.${entry.version}.json`);
  if (!res.ok) {
    throw new Error(`fetch ${cdnKey} failed (${res.status})`);
  }
  return (await res.json()) as T;
}
